import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import OrderApi from "../Api/OrderApi";
import { formatDate } from "../helper/DateHelper";

const CancelledOrderDetailsLayer = () => {
    const { id } = useParams();
    const [orderData, setOrderData] = useState(null);
    const [customerData, setCustomerData] = useState(null);
    const [loading, setLoading] = useState(true);
    const billRef = useRef();

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const res = await OrderApi.getOrderDetails(id);
            if (res.status && res.response) {
                setOrderData(res.response.data.order);
                setCustomerData(res.response.data.customer);
            }
        } catch (error) {
            console.error("Error fetching order details:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [id]);

    const handlePrint = () => {
        const printContent = billRef.current.innerHTML;
        const originalContent = document.body.innerHTML;
        document.body.innerHTML = printContent;
        window.print();
        document.body.innerHTML = originalContent;
        window.location.reload();
    };

    if (loading) return (
        <div className="d-flex justify-content-center align-items-center py-100">
            <div className="spinner-border text-primary-600" role="status"></div>
        </div>
    );

    if (!orderData) return <div className="text-center py-100">Order not found.</div>;

    return (
        <div className="row g-4 pb-40">
            <style>{`
                .pos-bill-container {
                    background: #fff;
                    padding: 30px;
                    border: 1px solid #e2e8f0;
                    border-radius: 0;
                    width: 100%;
                    max-width: 450px;
                    margin: 0 auto;
                    font-family: 'Inter', sans-serif;
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05);
                }
                .bill-header-text {
                    text-align: center;
                    margin-bottom: 16px;
                    border-bottom: 1px solid #e2e8f0;
                    padding-bottom: 16px;
                }
                .dashed-divider {
                    border-top: 1px dashed #cbd5e1;
                    margin: 16px 0;
                }
                @media print {
                    .no-print { display: none !important; }
                    .pos-bill-container { box-shadow: none; border: none; padding: 0; }
                }
                .pos-bill-container table thead,
                .pos-bill-container table thead tr {
                    background: transparent !important;
                    background-color: transparent !important;
                }
                .pos-bill-container table tbody tr {
                    background: transparent !important;
                    background-color: transparent !important;
                }
                .cancelled-badge-large {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 24px;
                    background: #fef2f2;
                    border: 2px solid #fee2e2;
                    border-radius: 12px;
                    color: #b91c1c;
                }
            `}</style>

            <div className="col-12">
                <div className="card border-0 shadow-sm radius-12 p-32">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-3">
                            <Link to="/cancelled-orders" className="btn btn-outline-neutral-600 w-48-px h-48-px d-flex justify-content-center align-items-center rounded-circle">
                                <Icon icon="lucide:arrow-left" className="text-xl" />
                            </Link>
                            <div>
                                <h4 className="mb-0 fw-bold text-primary-600">Order #{orderData.invoiceId || (orderData.id || orderData._id)?.toString().slice(-6).toUpperCase()}</h4>
                                <p className="text-secondary-light mb-0" style={{ fontSize: '13px' }}>Placed on {formatDate(orderData.createdAt)}</p>
                                <p className="text-danger-600 mt-1 fw-bold italic" style={{ fontSize: '13px' }}>
                                    Reason: <span className="text-secondary-light fw-medium">{orderData.cancelReason || 'Customer Request'}</span>
                                </p>
                            </div>
                        </div>
                        
                        <div className="cancelled-badge-large">
                            <Icon icon="solar:close-circle-bold" className="text-4xl" />
                            <div>
                                <h5 className="mb-0 fw-black uppercase letter-spacing-1">Order Cancelled</h5>
                                <p className="mb-0 text-sm opacity-75 fw-bold">Transaction Terminated</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-xl-8">
                <div className="card border-0 shadow-sm radius-12 p-32 h-100">
                    <div className="d-flex align-items-center justify-content-between mb-32">
                        <h6 className="mb-0 fw-bold text-primary-600 d-flex align-items-center gap-2">
                            <Icon icon="solar:bill-list-bold-duotone" className="text-2xl" />
                            Cancelled Order Invoice
                        </h6>
                        <button onClick={handlePrint} className="btn btn-primary-600 radius-8 px-24 py-11 fw-bold d-flex align-items-center gap-2">
                            <Icon icon="solar:printer-bold" className="text-xl" />
                            Print Bill
                        </button>
                    </div>

                    <div ref={billRef}>
                        <div className="pos-bill-container">
                            <div className="bill-header-text text-center">
                                <div className="mb-12">
                                    <span style={{ fontFamily: "'Aclonica', sans-serif", fontSize: '20px', fontWeight: '500', color: '#364e3d', letterSpacing: '0.05em', lineHeight: '1.2', whiteSpace: 'nowrap' }}>
                                        <span style={{ fontSize: '20px', fontWeight: '700' }}>P</span>RRAYASHA <span style={{ fontSize: '20px', fontWeight: '700' }}>C</span>OLLECTIONS
                                    </span>
                                </div>
                                <p className="text-xs text-secondary-light fw-bold mb-1">Provident Cosmo City, DR Abdul Kalam Road,</p>
                                <p className="text-xs text-secondary-500 mb-1">Pudhupakkam Village, Chengalpet 603103</p>
                                <p className="text-xs text-secondary-500 mb-0">PH: +91 91590 24967</p>
                                <div className="mt-8">
                                    <span style={{ fontSize: '10px' }} className="border border-secondary-100 text-secondary-light px-12 py-4 radius-4 fw-bold uppercase letter-spacing-1">Order Invoice</span>
                                </div>
                                <div className="mt-12 p-8 radius-4 bg-danger-50 border border-danger-100">
                                    <p className="text-danger-600 fw-bold uppercase mb-4" style={{ fontSize: '9px', letterSpacing: '1px' }}>Order Cancelled</p>
                                    <p className="text-secondary-light fw-medium italic mb-0" style={{ fontSize: '11px' }}>Reason: {orderData.cancelReason || 'Customer Request'}</p>
                                </div>
                            </div>

                            <div className="bill-info-grid">
                                <div className="d-flex justify-content-between mb-4">
                                    <span className="text-xs text-secondary-500 fw-medium">Invoice ID:</span>
                                    <span className="text-xs text-secondary-light fw-bold">#{orderData.invoiceId || (orderData.id || orderData._id)?.toString().slice(-6).toUpperCase()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-0">
                                    <span className="text-xs text-secondary-550 fw-medium">Date:</span>
                                    <span className="text-xs text-secondary-light fw-bold">{formatDate(orderData.createdAt)}</span>
                                </div>
                                <div className="dashed-divider my-12"></div>
                                <div className="mb-12">
                                    <span style={{ fontSize: '10px' }} className="text-secondary-400 fw-bold d-block mb-3 uppercase">Customer Details:</span>
                                    <h6 className="text-sm fw-bold text-secondary-light mb-1">{orderData.address?.name}</h6>
                                    <p className="text-xs text-secondary-500 mb-0 leading-relaxed">
                                        {orderData.address?.doorNo}, {orderData.address?.street},<br />
                                        {orderData.address?.city} - {orderData.address?.pincode}
                                    </p>
                                    <p className="text-xs text-secondary-550 mt-4 mb-0 fw-bold">Mob: {orderData.address?.phone}</p>
                                </div>
                            </div>

                            <table className="w-100 mb-12" style={{ backgroundColor: 'transparent' }}>
                                <thead className="border-bottom-1 border-secondary-500" style={{ background: 'transparent' }}>
                                    <tr style={{ background: 'transparent' }}>
                                        <th className="text-xs text-secondary-light fw-bold pb-8 text-center uppercase" style={{ width: '30px', minWidth: '30px' }}>S.No</th>
                                        <th className="text-xs text-secondary-light fw-bold pb-8 text-start uppercase" style={{ width: '100%' }}>Items</th>
                                        <th className="text-xs text-secondary-light fw-bold pb-8 text-center uppercase" style={{ width: '60px' }}>Qty</th>
                                        <th className="text-xs text-secondary-light fw-bold pb-8 text-end uppercase" style={{ width: '80px' }}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orderData.products.map((item, idx) => (
                                        <tr key={idx} style={{ background: 'transparent' }}>
                                            <td className="py-12 border-bottom-1 border-secondary-50 text-center">
                                                <span className="text-xs text-secondary-light fw-medium">{idx + 1}</span>
                                            </td>
                                            <td className="py-12 border-bottom-1 border-secondary-50">
                                                <span className="text-xs fw-bold text-secondary-light d-block">{item.productName}</span>
                                                {item.combination && item.combination.length > 0 && (
                                                    <div className="d-flex flex-wrap gap-1 mt-4">
                                                        {item.combination.map((c, i) => (
                                                            <span key={i} className="text-xxxxs border border-secondary-100 px-6 py-2 radius-4 text-secondary-400 fw-medium">
                                                                {c.value}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-12 border-bottom-1 border-secondary-50 text-center">
                                                <span className="text-xs text-secondary-light fw-medium">{item.qty}</span>
                                            </td>
                                            <td className="py-12 border-bottom-1 border-secondary-50 text-end">
                                                <span className="text-xs fw-bold text-secondary-light">₹{item.total?.toLocaleString()}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <div className="bill-calculation">
                                <div className="d-flex justify-content-between mb-8">
                                    <span className="text-xs text-secondary-550 fw-medium">Subtotal</span>
                                    <span className="text-xs text-secondary-light fw-bold">₹{orderData.totalAmount?.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-8">
                                    <span className="text-xs text-secondary-550 fw-medium">Tax</span>
                                    <span className="text-xs text-secondary-light fw-bold">₹{orderData.taxAmount?.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-8">
                                    <span className="text-xs text-secondary-550 fw-medium">Shipping</span>
                                    <span className="text-xs text-secondary-light fw-bold">₹{orderData.shippingCharge?.toLocaleString()}</span>
                                </div>
                                <div className="dashed-divider my-12"></div>
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-sm fw-bold text-secondary-light uppercase">Grand Total</span>
                                    <span className="text-lg fw-bold text-secondary-light">₹{orderData.grandTotal?.toLocaleString()}</span>
                                </div>
                                <div className="mt-20 text-center border-top-1 border-secondary-100 pt-16">
                                    <p className="text-xxxxs text-secondary-400 fst-italic mb-4 uppercase">Payment: {orderData.paymentMethod} | Status: {orderData.paymentStatus}</p>
                                    <p className="text-xxxxs fw-bold text-secondary-light uppercase">*** Thank you for your business! ***</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-xl-4 no-print">
                <div className="d-flex flex-column gap-4">
                    {/* Customer Info Card */}
                    <div className="card border-0 shadow-sm radius-12 p-32">
                        <h6 className="mb-20 fw-bold text-primary-600">Customer Statistics</h6>
                        {customerData ? (
                            <div className="d-flex flex-column gap-4">
                                <div className="d-flex align-items-center gap-3 p-16 radius-12 bg-neutral-50">
                                    <div className="w-48-px h-48-px rounded-circle bg-primary-100 d-flex align-items-center justify-content-center text-primary-600">
                                        <Icon icon="solar:user-bold" className="text-2xl" />
                                    </div>
                                    <div>
                                        <p className="text-xxxxs text-secondary-400 mb-0 fw-bold uppercase">Full Name</p>
                                        <h6 className="text-sm fw-bold text-secondary-light mb-0">{customerData.fullName}</h6>
                                    </div>
                                </div>

                                <div className="row g-3">
                                    <div className="col-6">
                                        <div className="p-16 radius-12 bg-success-50 h-100">
                                            <p className="text-xxxxs text-success-main mb-4 fw-bold uppercase">Total Orders</p>
                                            <h4 className="mb-0 fw-black text-success-main">{customerData.orderCount}</h4>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="p-16 radius-12 bg-info-50 h-100">
                                            <p className="text-xxxxs text-info-main mb-4 fw-bold uppercase">Order Value</p>
                                            <h4 className="mb-0 fw-black text-info-main">₹{orderData.grandTotal?.toLocaleString()}</h4>
                                        </div>
                                    </div>
                                </div>

                                <div className="dashed-divider"></div>

                                <div className="d-flex flex-column gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <Icon icon="solar:phone-bold" className="text-secondary-400 text-sm" />
                                        <span className="text-xs text-secondary-light fw-medium">{customerData.phoneNumber}</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Icon icon="solar:letter-bold" className="text-secondary-400 text-sm" />
                                        <span className="text-xs text-secondary-light fw-medium">{customerData.email || 'No Email'}</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Icon icon="solar:calendar-bold" className="text-secondary-400 text-sm" />
                                        <span className="text-xs text-secondary-light fw-medium">Joined {formatDate(customerData.createdAt)}</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-20 text-secondary-400 text-sm italic">No profile data available.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CancelledOrderDetailsLayer;

